/**
 * @file     listings.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for owner listings: kind labels, lifecycle status labels and end reasons, shared by the builder, the owner dashboard and admin review; plus the owner side (front door, estimate, builder steps, verify, your listings).
 */
export const listings = {
  kinds: {
    lease: { label: 'For rent', listedBy: 'Listed by Ray Rasouli' },
  },
  status: {
    draft: 'Draft',
    submitted: 'Submitted',
    agreement: 'Agreement',
    in_review: 'In review',
    changes_requested: 'Changes requested',
    live: 'Live',
    paused: 'Paused',
    ended: 'Ended',
    expired: 'Expired',
    withdrawn: 'Withdrawn',
    rejected: 'Not approved',
  },
  endReason: {
    leased: 'Leased',
    sold: 'Sold',
    other: 'Taken down',
  },
  owner: {
    reviewReasons: {
      photos_quality: 'Retake the dark or blurry photos in daylight.',
      wording_people: 'Describe the home, not the people who should live there.',
      details_missing: 'Fill in the missing or unclear details.',
      money_rules: 'Set the rent and deposits within BC rules. Deposits are capped at half a month.',
      address_mismatch: 'Check the address and unit match the home.',
      ownership_unverified: 'Ray could not confirm you own or manage this home.',
      not_residential_rental: 'Relta only lists homes for residential rent.',
      listed_elsewhere: 'This home is already listed for rent on MLS.',
      other: 'See the note from Ray below.',
    },
    door: {
      eyebrow: 'For owners',
      h1: 'Rent out your place',
      lede: 'See what it could rent for, then post it free. Ray Rasouli, REALTOR®, handles the search for a tenant.',
      metaTitle: 'Rent out your place in BC',
      metaDescription: 'See what homes like yours are listed for, then post your rental free. Ray Rasouli reviews every listing and handles enquiries and viewings.',
      doesTitle: 'What Ray does',
      does: {
        list: { title: 'Lists it with a rent range', line: 'Your home goes on relta.ca, priced against asking rents nearby.' },
        screen: { title: 'Answers enquiries and screens renters', line: 'Checks references and fit with your house rules, within the Human Rights Code.' },
        show: { title: 'Shows your home', line: 'Books and runs every viewing. Your contact details stay private.' },
        lease: { title: 'Prepares the lease', line: 'A BC tenancy agreement, ready for you and the tenant to sign.' },
        deposits: { title: 'Holds deposits through the brokerage', line: 'Security and pet deposits are held in trust.' },
      },
      notTitle: 'What Ray does not do',
      notLine: 'Collect rent, or manage the home and repairs after move-in.',
      notNote: 'Those need a licensed property manager. Rent goes from the tenant to you.',
      rentTitle: 'How the rent is set',
      rentLine: 'You see what similar homes nearby are listed for, then you set the rent. Renters cannot bid on it.',
      reviewTitle: 'Ray reviews every listing',
      reviewLine: 'Ray Rasouli checks the photos, details and wording before a listing goes live, then gets in touch with you.',
      reviewNote: 'Posting a listing does not mean anyone represents you. That starts only if you and Ray sign an agreement.',
      stepsTitle: 'How it works',
      steps: {
        estimate: { title: 'Get the estimate', line: 'A rent range for your home.' },
        build: { title: 'Build the listing', line: 'Saved as you go, on any device.' },
        verify: { title: 'Verify', line: 'Your phone and one statement.' },
        review: { title: 'Ray reviews it', line: 'Then he gets in touch with you.' },
        live: { title: 'It goes live', line: 'Ray handles enquiries and viewings.' },
      },
      closeTitle: 'Start with the estimate',
      closeLine: 'It takes the address and two answers.',
      cta: 'Rent out your place',
      continueTitle: 'Continue your listing',
      continueMeta: '{{address}} · {{pct}}%',
      continueCta: 'Continue',
    },
    estimate: {
      title: 'What will it rent for?',
      address: 'Address',
      addressPlaceholder: 'Street address, city',
      bedrooms: 'Bedrooms',
      studio: 'Studio',
      homeType: 'Home type',
      submit: 'Get the estimate',
      loading: 'Checking asking rents nearby',
      bandLine: 'Homes like this are listed for {{low}} to {{high}} a month',
      bandMethod: 'From {{count}} asking rents within {{radius}} km. An estimate, not an appraisal.',
      leasedTitle: 'Leased rents nearby',
      leasedLine: '{{low}} to {{high}} a month, from {{count}} leased homes within {{radius}} km.',
      leasedTerms: 'Leased MLS® rents shown under VOW terms. Only you and Ray see these.',
      seeLeased: 'See leased rents nearby',
      vowEyebrow: 'Leased rents',
      vowHeadline: 'See what homes like yours leased for',
      insufficient: 'There are not enough listed rentals like this nearby for a range. You can still post it, and Ray will help you price it.',
      notFound: 'That address was not found. Pick one from the list.',
      error: 'The estimate did not load. Try again.',
      summary: '{{address}} · {{beds}} {{type}}',
      beds_one: '{{count}} bed',
      beds_other: '{{count}} bed',
      signedOutTitle: 'Create a free account to see the range',
      signedOutLine: 'The range uses MLS® data, which needs an account.',
      signedOutCta: 'Create a free account',
      start: 'Rent out your place',
      starting: 'Starting your listing',
      startError: 'Your listing did not start. Try again.',
      draftLimit: 'You have reached the limit of open listings. Finish or withdraw one first.',
      homeTypes: { apartment_condo: 'Apartment', townhouse: 'Townhouse', house: 'House', suite: 'Suite', laneway: 'Laneway', duplex: 'Duplex', other: 'Other' },
    },
    entry: {
      ownerHomeTitle: 'Rent out this home',
      ownerHomeLine: 'Post it free. Ray Rasouli handles enquiries, viewings and the lease.',
      landlordTitle: 'List it with Ray Rasouli',
      landlordLine: 'Post your place free and let Ray handle viewings and the lease.',
      cta: 'Rent out your place',
    },
    builder: {
      product: 'Rent out your place',
      kindChip: 'For rent',
      saved: 'Saved',
      savedLong: 'Saved. Pick up on any device.',
      saving: 'Saving',
      saveFailed: 'Your last change did not save',
      saveFailedLine: 'Check your connection. It retries on its own.',
      retry: 'Retry',
      saveAndExit: 'Save and exit',
      close: 'Close',
      pctComplete: '{{pct}}% complete',
      stepsLeft_one: '{{count}} of {{total}} steps left',
      stepsLeft_other: '{{count}} of {{total}} steps left',
      drafted: 'Listing drafted',
      afterDrafting: 'after drafting',
      phoneTitle: '{{n}} of {{total}} · {{step}}',
      questions: 'Questions?',
      askRay: 'Ask Ray Rasouli',
      back: 'Back',
      continue: 'Continue',
      continueVerify: 'Continue to verify',
      submit: 'Submit for review',
      submitting: 'Submitting',
      resubmit: 'Send it back to Ray',
      done: 'Done',
      stepNav: 'Listing steps',
      steps: { address: 'Address', details: 'Details', photos: 'Photos', description: 'Description', pricing: 'Pricing', review: 'Review', verify: 'Verify' },
      fixFirst: 'Finish this step to continue.',
      hints: {
        address: 'Renters see the street, never the house number.',
        photos: '{{count}} of {{min}} photos.',
        photosFailed: '{{count}} of {{min}} photos. Some did not upload.',
        description: '{{count}} wording flag to look at.',
        description_other: '{{count}} wording flags to look at.',
        review: 'Listing drafted. Verify comes next.',
        verify: 'Ray reviews it after you submit.',
        liveEdit: 'Renters see the live version until Ray approves your change.',
      },
      changesTitle: 'Ray asked for changes',
      changesLine: 'Make these changes, then send it back.',
      liveEditTitle: 'This listing is live',
      liveEditLine: 'Your edits go to Ray first. Renters see the live version until he approves them.',
      lockedTitle: 'This listing cannot be edited now',
      lockedLine: 'It is {{status}}. You can see it in your listings.',
      backToListings: 'Your listings',
      loadError: 'This listing did not load.',
      submittedTitle: 'Submitted for review',
      submittedLine: 'Ray Rasouli reviews every listing and will contact you.',
      submittedCta: 'See your listings',
      submitError: 'It did not submit. Check the steps marked below.',
      notFoundTitle: 'Listing not found',
    },
    address: {
      title: 'Where is the home?',
      lede: 'Strata homes need the unit.',
      label: 'Address',
      placeholder: 'Start typing the street address',
      unit: 'Unit',
      unitHelp: 'Leave blank for a house.',
      carried: 'Address carried over from your estimate.',
      notFound: 'Pick the address from the list so it can be found on the map.',
      outsideBc: 'Relta lists homes in British Columbia only.',
      checking: 'Checking the MLS® feed',
      none: 'No active MLS® listing for this home',
      noneLine: 'Checked by address against the MLS® feed.',
      blocked: 'This home is listed on the MLS®',
      blockedLine: 'It is listed by {{brokerage}}. It cannot be posted here while that listing is active.',
      blockedLineNoBrokerage: 'It cannot be posted here while that listing is active.',
      linked: 'This home is on the MLS® with Ray Rasouli',
      linkedLine: 'Ray links the two listings when he reviews it.',
      checkFailed: 'The MLS® check did not run. It runs again when you continue.',
      private: 'Renters see the street and unit, never the house number.',
    },
    details: {
      title: 'About the rental',
      lede: 'Renters filter on these. Rent is set in Pricing.',
      optional: 'Optional',
      required: 'Choose one',
      invalid: 'Check this value',
      studio: 'Studio',
      sqft: 'sq ft',
    },
    fields: {
      homeType: { label: 'Home type', why: 'Renters search by type first.', options: { apartment_condo: 'Apartment or condo', townhouse: 'Townhouse', house: 'House', suite: 'Suite', laneway: 'Laneway', duplex: 'Duplex', other: 'Other' } },
      bedrooms: { label: 'Bedrooms', why: 'The most used rental filter.' },
      bathrooms: { label: 'Bathrooms', why: 'Half baths count as 0.5.' },
      livingArea: { label: 'Size', why: 'Helps renters compare homes.' },
      availableOn: { label: 'Available', why: 'Renters filter by move-in date.' },
      leaseTerm: { label: 'Lease term', why: 'Shown on the listing so renters know what to expect.', options: { one_year: '1-year fixed term', six_months: '6-month fixed term', month_to_month: 'Month-to-month', other: 'Other term' } },
      furnished: { label: 'Furnished', why: 'A common filter for shorter moves.', options: { unfurnished: 'No', furnished: 'Yes', partly: 'Partly' } },
      pets: { label: 'Pets', why: 'Renters with pets filter on this.', options: { yes: 'Cats and dogs', cats: 'Cats', dogs: 'Dogs', no: 'No pets', ask: 'Ask first' } },
      laundry: { label: 'Laundry', why: 'In-suite laundry is a top filter.', options: { in_unit: 'In suite', in_building: 'Shared in building', none: 'None' } },
      parking: { label: 'Parking', why: 'Spaces included with the rent.' },
      utilitiesIncluded: { label: 'Utilities included', why: 'Helps renters compare the true monthly cost.', options: { heat: 'Heat', hot_water: 'Hot water', electricity: 'Electricity', water: 'Water', gas: 'Gas', internet: 'Internet' } },
      rules: { label: 'House rules', why: 'Rules about the home, such as no smoking. Never about who may live there.', placeholder: 'No smoking. No short-term sublets.' },
    },
    photos: {
      title: 'Photos',
      lede: 'Add at least {{min}}. Drag to set the order; the first one is the cover.',
      ledePhone: 'Add at least {{min}}. The first one is the cover.',
      count: '{{count}} of {{min}} minimum',
      drop: 'Drag photos here or browse',
      dropLine: 'JPG, PNG or HEIC, up to {{max}}. Location data is removed.',
      add: 'Add photos',
      cover: 'Cover',
      dark: 'Looks dark',
      lowRes: 'Low resolution',
      uploading: 'Uploading {{pct}}%',
      converting: 'Converting HEIC',
      processing: 'Processing',
      failed: 'Did not upload',
      retry: 'Retry',
      remove: 'Remove photo {{n}}',
      move: 'Photo {{n}}. Press space to pick it up, then use the arrow keys to move it.',
      tips: 'Tips: shoot in daylight, show every room, the view and the building.',
      tipsFlagged: 'Some photos look dark or are under 1600 px wide. Retake them if you can.',
      longPress: 'Long-press a photo to move it.',
      tooMany: 'Up to {{max}} photos.',
      wrongType: '{{name}} is not a JPG, PNG or HEIC photo.',
      tooBig: '{{name}} is over 25 MB.',
      reorderFailed: 'The new order did not save. Try again.',
      removeFailed: 'The photo was not removed. Try again.',
      reasons: {
        unsupported_or_too_small: 'Too small. Use a photo at least 640 px on each side.',
        size_mismatch: 'The file changed during upload. Try again.',
        decode_failed: 'This photo could not be read.',
        not_uploaded: 'The upload did not finish.',
        max: 'Up to 30 photos.',
      },
    },
    description: {
      title: 'Describe the home',
      lede: 'Write about the home itself. Ray reviews every listing before it goes live.',
      headline: 'Headline',
      headlineHelp: 'Up to 80 characters.',
      body: 'Description',
      bodyHelp: 'Do not add phone numbers or emails. Enquiries go to Ray.',
      counter: '{{n}} / 2,000',
      tooShort: 'Write at least {{min}} characters.',
      contact: 'Take out the phone number or email. Enquiries go to Ray.',
      draftFromDetails: 'Draft from my details',
      draftConfirm: 'Replace what you wrote with a draft from your details?',
      flagTitle: 'Describes people, not the home',
      flagTry: 'Try',
      flagRemove: 'Remove the phrase.',
      useSuggestion: 'Use suggestion',
      editMyself: 'Edit it myself',
      checks: 'Checks',
      checkContact: 'No phone numbers or emails',
      checkPeople_zero: 'No phrases about people',
      checkPeople_one: '{{count}} phrase about people',
      checkPeople_other: '{{count}} phrases about people',
      checking: 'Checking the wording',
      nothingChanges: 'Nothing changes without your tap. Ray sees the same checks in review.',
    },
    pricing: {
      title: 'Set the rent',
      lede: 'You set the rent. Renters cannot bid on it.',
      askingTitle: 'Asking rents nearby',
      askingLine: '{{low}} to {{high}} a month',
      askingMethod: 'From {{count}} listed homes like yours within {{radius}} km.',
      askingNone: 'Not enough listed homes like yours nearby for a range.',
      askingNeeds: 'Add the address, bedrooms and home type to see asking rents nearby.',
      askingError: 'Asking rents did not load.',
      leasedTitle: 'Leased comparables',
      leasedLine: '{{low}} to {{high}} a month, from {{count}} leased homes within {{radius}} km.',
      leasedTerms: 'Leased MLS® rents shown under VOW terms. Only you and Ray see this list.',
      leasedLocked: 'See what homes like yours leased for',
      leasedNone: 'Not enough leased homes like yours nearby.',
      comparableSpec: '{{beds}} bd · {{baths}} ba',
      listedAt: 'Listed at {{rent}}',
      rent: 'Monthly rent',
      perMonth: '/month',
      inRange: 'In the usual range',
      above: 'Above asking rents nearby',
      below: 'Below asking rents nearby',
      rentRange: 'Enter a rent from {{min}} to {{max}}.',
      security: 'Security deposit',
      pet: 'Pet deposit',
      upTo: 'Up to {{cap}}',
      overCap: 'BC caps this at half a month: {{cap}}.',
      cap: 'BC maximum: half a month each. The rent you set is the rent.',
      optional: 'Optional',
    },
    review: {
      title: 'Review your listing',
      lede: 'Check it the way renters will see it.',
      preview: 'Open the preview',
      previewLine: 'The public page, as renters will see it.',
      ready: 'Ready for Ray\'s review',
      notReady: 'Still to do',
      checkPhotos: '{{count}} photos, cover set',
      checkDetails: 'Every required detail',
      checkDescription: 'Description checks passed',
      checkRent: 'Rent set: {{rent}} a month',
      checkAddress: 'Address found',
      checkMls: 'No active MLS® listing',
      missingPhotos: 'Add at least {{min}} photos',
      missingDetails: 'Finish the details',
      missingDescription: 'Finish the description',
      missingRent: 'Set the rent',
      missingAddress: 'Add the address',
      next: 'Next: verify your phone and confirm the home is yours to rent. Then it goes to Ray for review.',
      edit: 'Edit',
      summaryRent: '{{rent}}/month',
      summaryAvailable: 'Available {{date}}',
    },
    verify: {
      title: 'Verify it is your home',
      lede: 'Two checks before Ray reviews it.',
      phoneTitle: '1. Your mobile phone',
      phone: 'Mobile phone',
      sendCode: 'Send code',
      sending: 'Sending',
      codeSent: 'Enter the 6-digit code sent to {{phone}}.',
      code: 'Code',
      checkCode: 'Verify',
      checking: 'Checking',
      resendIn: 'No code? Send a new code in {{time}}',
      resend: 'Send a new code',
      changePhone: 'Use a different number',
      verified: 'Phone verified',
      invalidPhone: 'Enter a mobile number with the area code.',
      invalidCode: 'That code did not match. Try again.',
      rateLimited: 'Too many tries. Wait a few minutes, then try again.',
      sendFailed: 'The code did not send. Try again.',
      statementTitle: '2. Your statement',
      role: 'You are',
      roleOwner: 'The owner',
      roleAuthorised: 'Authorised to rent it',
      legalName: 'Your legal name',
      legalNameHelp: 'As it appears on your ID.',
      legalNameInvalid: 'Enter your full legal name.',
      statement: 'I own this home or am authorised to rent it.',
      confirm: 'I confirm this is true.',
      save: 'Save statement',
      saving: 'Saving',
      saved: 'Statement saved: {{name}}',
      saveFailed: 'The statement did not save. Try again.',
      rayChecks: 'Ray confirms ownership during his review.',
    },
    dashboard: {
      title: 'Your listings',
      lede: 'Homes Ray Rasouli lists for you. Enquiries go to Ray; your contact details stay private.',
      another: 'Rent out another place',
      emptyTitle: 'No listings yet',
      emptyLine: 'See what your place could rent for.',
      emptyCta: 'Rent out your place',
      loadError: 'Your listings did not load.',
      untitled: 'New listing',
      draftLine: '{{pct}}% done. {{step}} is next.',
      draftLineDone: '{{pct}}% done. Verify and submit.',
      continue: 'Continue your listing',
      inReviewLine: 'Ray is reviewing it and will contact you.',
      liveLine: '{{rent}}/mo · Live since {{since}} · Ends {{ends}}',
      pausedLine: 'Paused. Renters cannot see it.',
      endedLeased: 'Leased {{date}}{{rent}}. Taken down the same day.',
      endedLeasedRent: ' at {{rent}} a month',
      endedOther: 'Taken down {{date}}.',
      expiredLine: 'Expired {{date}}. Relist it to send it back to Ray.',
      withdrawnLine: 'Withdrawn {{date}}.',
      rejectedLine: 'Ray did not approve this listing.',
      changesLine: 'Ray asked for changes.',
      fix: 'Fix and resubmit',
      edit: 'Edit listing',
      pause: 'Pause',
      resume: 'Resume',
      markLeased: 'Mark as leased',
      relist: 'Relist',
      withdraw: 'Withdraw',
      withdrawConfirm: 'Withdraw this listing? It comes off the site and Ray stops working on it.',
      viewPublic: 'See it live',
      actionFailed: 'That did not work. Try again.',
      relistNeeds: 'Relisting needs every step done. Opening your listing.',
      reasonsTitle: 'What to change',
      noteTitle: 'Note from Ray',
      timelineLabel: 'Listing status',
      stats: { views: 'Views', saves: 'Saves', enquiries: 'Enquiries', viewingRequests: 'Viewing requests' },
      statsNote: 'Ray replies to every renter.',
      pendingLive: 'Live now',
      pendingWaiting: 'Waiting for Ray\'s review',
      pendingLine: 'Renters see the live version until Ray approves the change.',
      pendingRent: 'Rent {{rent}} a month',
      pendingAvailable: 'Available {{date}}',
      pendingHeadline: 'Headline: {{text}}',
      pendingChanged: '{{field}} changed',
      keepTitle: 'Keep renting',
      keepFigure: '{{amount}} a year in rent',
      keepNote: 'Before strata, tax and upkeep',
      sellTitle: 'Or sell',
      sellLine: 'See what it could sell for',
      sellNote: 'From nearby sales, with a free account',
      sellCta: 'Get a home value report',
    },
    ended: {
      title: 'Mark as leased',
      lede: 'This takes {{address}} down today and tells Ray.',
      rent: 'Leased for (optional)',
      rentHelp: 'Monthly rent. Only Ray sees this.',
      starts: 'Tenancy starts (optional)',
      submit: 'Mark as leased',
      keep: 'Keep it live',
      failed: 'It did not update. Try again.',
    },
    timeline: {
      draft: 'Draft',
      in_review: 'In review',
      changes_requested: 'Changes requested',
      live: 'Live',
      paused: 'Paused',
      leased: 'Leased',
      ended: 'Ended',
      expired: 'Expired',
      withdrawn: 'Withdrawn',
      rejected: 'Not approved',
    },
    steps: { address: 'The address', details: 'The details', photos: 'Photos', description: 'The description', pricing: 'The rent', review: 'Review' },
    draft: {
      headline: '{{beds}} {{type}} on {{street}}',
      headlineStudio: 'Studio {{type}} on {{street}}',
      beds_one: '{{count}}-bedroom',
      beds_other: '{{count}}-bedroom',
      opening: 'A {{beds}} {{type}} for rent on {{street}} in {{city}}.',
      openingStudio: 'A studio {{type}} for rent on {{street}} in {{city}}.',
      baths_one: 'It has {{count}} bathroom.',
      baths_other: 'It has {{count}} bathrooms.',
      bathsSize_one: 'It has {{count}} bathroom and about {{size}} sq ft of space.',
      bathsSize_other: 'It has {{count}} bathrooms and about {{size}} sq ft of space.',
      laundry: { in_unit: 'In-suite laundry.', in_building: 'Shared laundry in the building.', none: '' },
      parking_zero: '',
      parking_one: 'One parking space is included.',
      parking_other: '{{count}} parking spaces are included.',
      utilities: '{{list}} included in the rent.',
      furnished: { furnished: 'Furnished.', partly: 'Partly furnished.', unfurnished: 'Unfurnished.' },
      pets: { yes: 'Cats and dogs are welcome.', cats: 'Cats are welcome.', dogs: 'Dogs are welcome.', no: 'No pets.', ask: 'Ask about pets.' },
      available: 'Available {{date}}.',
      term: { one_year: '1-year fixed-term lease.', six_months: '6-month fixed-term lease.', month_to_month: 'Month-to-month tenancy.', other: '' },
      types: { apartment_condo: 'condo', townhouse: 'townhouse', house: 'house', suite: 'suite', laneway: 'laneway home', duplex: 'duplex', other: 'home' },
    },
  },
} as const;
